import { Link } from "react-router-dom";
import { ExternalLink, Landmark, FileText, HandCoins, GraduationCap, Building, Scale } from "lucide-react";

const GROUPS = [
  {
    icon: Landmark, title: "Arizona TPT Filing & Payment",
    links: [
      ["AZTaxes.gov — File & Pay TPT Online", "https://aztaxes.gov/", "Arizona's official portal for Transaction Privilege Tax filing, payments, and license management."],
      ["Arizona Department of Revenue (ADOR)", "https://azdor.gov/", "TPT rates, rules, notices, and taxpayer services."],
      ["TPT Rate Tables & Jurisdiction Codes", "https://azdor.gov/transaction-privilege-tax/tpt-rate-table", "Current combined state/county/city rates by region code."],
      ["TPT License Renewal & Updates", "https://azdor.gov/transaction-privilege-tax/tpt-license", "Renew or update your Arizona TPT license."],
    ],
  },
  {
    icon: HandCoins, title: "Behind on Taxes? Relief & Payment Options",
    links: [
      ["ADOR Payment Plans", "https://azdor.gov/individuals/payment-plan", "Set up an installment agreement for past-due Arizona tax balances."],
      ["ADOR Penalty Abatement Information", "https://azdor.gov/faqs/penalty-abatement", "How to request penalty relief for reasonable cause."],
      ["ADOR Voluntary Disclosure Program", "https://azdor.gov/business/voluntary-disclosure", "Come forward on unfiled periods with reduced penalties."],
      ["IRS Payment Plans & Installment Agreements", "https://www.irs.gov/payments/payment-plans-installment-agreements", "Federal options for past-due business taxes."],
      ["IRS Offer in Compromise", "https://www.irs.gov/payments/offer-in-compromise", "Settle qualifying federal tax debt for less than the full amount."],
    ],
  },
  {
    icon: FileText, title: "Business Registration & IDs",
    links: [
      ["IRS — Apply for an EIN Online", "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online", "Free federal Employer Identification Number application."],
      ["Arizona Corporation Commission", "https://azcc.gov/", "Entity formation, annual reports, and name registration."],
      ["Arizona Business One Stop", "https://az360.az.gov/", "Register a new Arizona business across agencies in one place."],
    ],
  },
  {
    icon: GraduationCap, title: "Free Help & Education",
    links: [
      ["ADOR Taxpayer Education & Webinars", "https://azdor.gov/news-events-notices/tpt-updates", "Workshops on TPT filing, deductions, and compliance."],
      ["Arizona SBDC Network", "https://azsbdc.net/", "Free small-business counseling across Arizona."],
      ["SCORE Arizona Mentors", "https://www.score.org/arizona", "Free mentoring from experienced business professionals."],
      ["SBA Arizona District Office", "https://www.sba.gov/district/arizona", "Loans, programs, and local assistance."],
      ["Arizona Commerce Authority — Small Business", "https://www.azcommerce.com/small-business/", "State programs, incentives, and the Small Business Ombudsman."],
    ],
  },
];

export default function Resources() {
  return (
    <div>
      <section className="border-b border-slate-200 bg-white py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-copper">Resources</div>
          <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">Arizona Tax Relief & Business Resources</h1>
          <p className="mt-4 max-w-2xl text-slate-600">Official state and federal links for filing, paying, getting relief on past-due taxes, and registering your Arizona business. These links go directly to government and nonprofit sources.</p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <div className="grid gap-6 lg:grid-cols-2">
          {GROUPS.map((g) => (
            <div key={g.title} className="card-gov p-6" data-testid={`resource-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white"><g.icon className="h-5 w-5" /></div>
                <h2 className="text-lg font-bold text-navy">{g.title}</h2>
              </div>
              <ul className="mt-4 space-y-3">
                {g.links.map(([label, url, desc]) => (
                  <li key={url} className="rounded-md border border-slate-200 p-3 transition-colors hover:border-navy/40 hover:bg-navy-50">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 font-semibold text-navy" data-testid={`resource-${label.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}>
                      {label} <ExternalLink className="h-3.5 w-3.5 text-copper" />
                    </a>
                    <p className="mt-1 text-sm text-slate-500">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-md border border-copper/40 bg-copper-50 p-6">
          <div className="flex items-center gap-3">
            <Scale className="h-8 w-8 text-copper" />
            <div>
              <div className="font-bold text-navy">Rather have a professional untangle it?</div>
              <div className="text-sm text-slate-600">AZ Tax Rescue reconstructs missing periods and builds your path to current.</div>
            </div>
          </div>
          <Link to="/tax-rescue" className="btn-copper" data-testid="resources-rescue-cta">Open a Tax Rescue Case</Link>
        </div>
      </section>
    </div>
  );
}
